import { Component, OnInit, inject, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UsersService } from '../../../../core/services/users.service';
import { AuthService } from '../../../../core/services/auth.service';
import { User } from '../../../../core/models/user.interface';
import { LoaderService } from '../../../../core/shared/services/loader.service';
import { BaseResponse } from '../../../../core/models/base.response';
import { RecoverPasswordRequest } from '../../../../core/models/auth/auth.request';
import Swal from 'sweetalert2';
// Importar MatIconModule si se usan iconos de Material
// import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-profile-mentor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    // MatIconModule // Añadir si es necesario
  ],
  templateUrl: './profile-mentor.component.html',
  styleUrls: ['./profile-mentor.component.scss']
})
export class ProfileMentorComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private usersService = inject(UsersService);
  private authService = inject(AuthService);
  private loader = inject(LoaderService);
  private cdr = inject(ChangeDetectorRef);

  currentUser: User | null = null;
  profileForm!: FormGroup;
  isLoading = true;
  profileImageBase64: string | null = null;
  
  
  // Etiquetas disponibles para seleccionar
  availableTags: string[] = [
    'Liderazgo',
    'Comunicación',
    'Gestión de Tiempo',
    'Trabajo en Equipo',
    'Resolución de Problemas',
    'Creatividad',
    'Adaptabilidad',
    'Pensamiento Crítico',
    'Inteligencia Emocional',
    'Negociación',
    'Gestión de Proyectos',
    'Coaching',
    'Desarrollo Personal',
    'Estrategia Empresarial',
    'Marketing Digital',
    'Ventas',
    'Finanzas',
    'Recursos Humanos',
    'Tecnología',
    'Innovación'
  ];

  // Descripciones profesionales disponibles para seleccionar
  availableDescriptions: string[] = [
    'Licenciado en nutrición por la Universidad Peruana Cayetano Heredia y colegiatura habilitado (CNP 9236)',
    'Con experiencia en el tratamiento clínico especializado en obesidad y diabetes',
    'Especialista en hipotiroidismo y enfermedades cardiovasculares',
    'Experiencia en salud comunitaria y educación alimentaria nutricional',
    'Desarrollador de talleres de capacitación virtuales y presenciales',
    'Experto en población vulnerable migrante',
    'Profesional con más de 10 años de experiencia en el sector',
    'Certificado en coaching nutricional y bienestar integral',
    'Especialista en programas de prevención y promoción de la salud',
    'Experto en consultoría nutricional para empresas',
    'Facilitador de programas de cambio de hábitos alimentarios',
    'Especialista en nutrición deportiva y rendimiento',
    'Experto en trastornos alimentarios y conducta alimentaria',
    'Consultor en políticas públicas de nutrición y salud',
    'Especialista en nutrición pediátrica y materno-infantil'
  ];
  
  // Para controlar la visibilidad del dropdown
  showTagsDropdown = false;
  showDescriptionsDropdown = false;

  constructor() {}

  ngOnInit(): void {
    this.currentUser = this.authService.getUser();
    if (!this.currentUser || (this.currentUser.type.name !== 'Coach' && this.currentUser.type.name !== 'Experto')) {
      this.router.navigate(['/dashboard/profile']); // Redirigir si no es coach o experto
      return;
    }
    this.initForm();
    this.loadCoachProfile();
  }

  initForm(): void {
    this.profileForm = this.fb.group({
      // Campos del nuevo diseño
      fullName: [''], // Readonly, no requiere validación
      profession: ['', [Validators.required]],
      phone: ['', [Validators.required]],
      linkedinUrl: ['', [Validators.required, Validators.pattern(/.*www\.linkedin\.com.*/i)]],
      email: ['', [Validators.email]],
      description: ['', [Validators.required]],
      
      // Arrays para logros y datos curiosos
      achievements: this.fb.array([]),
      curiousFacts: this.fb.array([]),
      
      // Campos existentes (mantener para compatibilidad)
      professionalTitle: [''],
      contactLinks: this.fb.group({
        email: ['', [Validators.email]],
        linkedin: [''],
        website: ['']
      }),
      yearsOfExperience: [''],
      ranking: [''],
      mentorshipsCompleted: [''],
      aboutMentor: ['', [Validators.maxLength(1000)]],
      professionalDescription: [[]],  // Array de descripciones profesionales seleccionadas
      professionalDescriptionText: [''],  // Texto del textarea para mostrar/editar
      experienceSummary: [[]],  // Array de resúmenes de experiencia seleccionados
      experienceSummaryText: [''],  // Texto del textarea para mostrar/editar
      specialtyTags: this.fb.array([]),  // Array de etiquetas seleccionadas
      eventsAndAppearances: this.fb.array([])
    }, {
      validators: [
        this.validateAchievements.bind(this),
        this.validateCuriousFacts.bind(this),
        this.validateSpecialtyTags.bind(this)
      ]
    });

    // Suscribirse a cambios en el formulario para debug
    this.profileForm.statusChanges.subscribe(status => {
      console.log('[Profile Form] Estado del formulario:', status);
      console.log('[Profile Form] Formulario válido:', this.profileForm.valid);
      console.log('[Profile Form] Formulario inválido:', this.profileForm.invalid);
      this.logFormValidation();
    });

    // Suscribirse a cambios de valores para debug
    this.profileForm.valueChanges.subscribe(value => {
      console.log('[Profile Form] Valores del formulario cambiaron');
      this.logFormValidation();
    });
  }

  // Validadores personalizados
  validateAchievements(group: FormGroup): { [key: string]: any } | null {
    const achievements = group.get('achievements') as FormArray;
    const hasValidAchievement = achievements.controls.some(control => {
      const value = control.value;
      return value && typeof value === 'string' && value.trim().length > 0;
    });
    
    console.log('[Validation] Achievements - hasValidAchievement:', hasValidAchievement, 'values:', achievements.value);
    
    if (!hasValidAchievement) {
      return { achievementsRequired: true };
    }
    return null;
  }

  validateCuriousFacts(group: FormGroup): { [key: string]: any } | null {
    const curiousFacts = group.get('curiousFacts') as FormArray;
    const hasValidFact = curiousFacts.controls.some(control => {
      const value = control.value;
      return value && typeof value === 'string' && value.trim().length > 0;
    });
    
    console.log('[Validation] CuriousFacts - hasValidFact:', hasValidFact, 'values:', curiousFacts.value);
    
    if (!hasValidFact) {
      return { curiousFactsRequired: true };
    }
    return null;
  }

  validateSpecialtyTags(group: FormGroup): { [key: string]: any } | null {
    const specialtyTags = group.get('specialtyTags') as FormArray;
    const hasValidTag = specialtyTags.controls.some(control => {
      const value = control.value;
      return value && typeof value === 'string' && value.trim().length > 0;
    });
    
    console.log('[Validation] SpecialtyTags - hasValidTag:', hasValidTag, 'values:', specialtyTags.value);
    
    if (!hasValidTag) {
      return { specialtyTagsRequired: true };
    }
    return null;
  }

  // Método para loggear el estado de validación
  logFormValidation(): void {
    console.log('=== VALIDACIÓN DEL FORMULARIO ===');
    console.log('Formulario válido:', this.profileForm.valid);
    console.log('Formulario inválido:', this.profileForm.invalid);
    console.log('Errores del formulario:', this.profileForm.errors);
    
    // Validar cada campo individual
    const fields = ['profession', 'phone', 'linkedinUrl', 'description'];
    fields.forEach(field => {
      const control = this.profileForm.get(field);
      if (control) {
        console.log(`Campo ${field}:`, {
          valor: control.value,
          válido: control.valid,
          inválido: control.invalid,
          errores: control.errors,
          touched: control.touched,
          dirty: control.dirty
        });
      }
    });

    // Validar arrays
    console.log('Achievements:', {
      length: this.achievementsFormArray.length,
      values: this.achievementsFormArray.value,
      válidos: this.achievementsFormArray.controls.map(c => c.valid)
    });

    console.log('CuriousFacts:', {
      length: this.curiousFactsFormArray.length,
      values: this.curiousFactsFormArray.value,
      válidos: this.curiousFactsFormArray.controls.map(c => c.valid)
    });

    console.log('SpecialtyTags:', {
      length: this.specialtyTagsFormArray.length,
      values: this.specialtyTagsFormArray.value,
      válidos: this.specialtyTagsFormArray.controls.map(c => c.valid)
    });

    console.log('=== FIN VALIDACIÓN ===');
  }

  get eventsAndAppearances(): FormArray {
    return this.profileForm.get('eventsAndAppearances') as FormArray;
  }

  get achievementsFormArray(): FormArray {
    return this.profileForm.get('achievements') as FormArray;
  }

  get curiousFactsFormArray(): FormArray {
    return this.profileForm.get('curiousFacts') as FormArray;
  }

  newEvent(): FormGroup {
    return this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      imageUrl: [''],
      eventDate: ['']
    });
  }

  addEvent(): void {
    this.eventsAndAppearances.push(this.newEvent());
  }

  removeEvent(index: number): void {
    this.eventsAndAppearances.removeAt(index);
  }

  // Métodos para manejar las etiquetas
  get specialtyTagsFormArray(): FormArray {
    return this.profileForm.get('specialtyTags') as FormArray;
  }

  get selectedTags(): string[] {
    if (!this.profileForm) {
      return [];
    }
    return (this.specialtyTagsFormArray.value as string[]).filter((tag) => tag !== null && tag !== undefined);
  }

  toggleTag(tag: string): void {
    if (!this.profileForm) {
      return;
    }

    const existingIndex = this.specialtyTagsFormArray.controls.findIndex(control => control.value === tag);

    if (existingIndex > -1) {
      // Si la etiqueta ya está seleccionada, la removemos
      this.specialtyTagsFormArray.removeAt(existingIndex);
    } else if (this.specialtyTagsFormArray.length < 5) {
      // Si la etiqueta no está seleccionada, la agregamos
      this.specialtyTagsFormArray.push(this.fb.control(tag));
    }
  }

  isTagSelected(tag: string): boolean {
    return this.selectedTags.includes(tag);
  }

  toggleTagsDropdown(): void {
    this.showTagsDropdown = !this.showTagsDropdown;
  }

  removeTag(tag: string): void {
    if (!this.profileForm) {
      return;
    }

    const index = this.specialtyTagsFormArray.controls.findIndex(control => control.value === tag);
    if (index > -1) {
      this.specialtyTagsFormArray.removeAt(index);
    }
  }

  // Métodos para manejar las descripciones profesionales
  get selectedDescriptions(): string[] {
    return this.profileForm.get('professionalDescription')?.value || [];
  }

  toggleDescription(description: string): void {
    const currentDescriptions = this.selectedDescriptions;
    const index = currentDescriptions.indexOf(description);
    
    if (index > -1) {
      // Si la descripción ya está seleccionada, la removemos
      currentDescriptions.splice(index, 1);
    } else {
      // Si la descripción no está seleccionada, la agregamos
      currentDescriptions.push(description);
    }
    
    this.profileForm.get('professionalDescription')?.setValue([...currentDescriptions]);
  }

  isDescriptionSelected(description: string): boolean {
    return this.selectedDescriptions.includes(description);
  }

  toggleDescriptionsDropdown(): void {
    this.showDescriptionsDropdown = !this.showDescriptionsDropdown;
  }

  removeDescription(description: string): void {
    const currentDescriptions = this.selectedDescriptions;
    const index = currentDescriptions.indexOf(description);
    if (index > -1) {
      currentDescriptions.splice(index, 1);
      this.profileForm.get('professionalDescription')?.setValue([...currentDescriptions]);
      this.updateDescriptionsText();
    }
  }

  // Método para procesar el texto cuando cambie
  onDescriptionsTextChange(): void {
    const textValue = this.profileForm.get('professionalDescriptionText')?.value || '';
    
    if (textValue.trim()) {
      // Dividir por comas y limpiar espacios
      const items = textValue
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
      
      // Actualizar el formulario
      this.profileForm.get('professionalDescription')?.setValue([...items]);
    } else {
      // Si está vacío, limpiar la lista
      this.profileForm.get('professionalDescription')?.setValue([]);
    }
  }

  // Método para actualizar el texto cuando se remueve un elemento
  updateDescriptionsText(): void {
    const textValue = this.selectedDescriptions.join(', ');
    this.profileForm.get('professionalDescriptionText')?.setValue(textValue);
  }

  // Métodos para manejar el resumen de experiencia
  get selectedExperienceSummaries(): string[] {
    return this.profileForm.get('experienceSummary')?.value || [];
  }

  // Método para procesar el texto del resumen de experiencia cuando cambie
  onExperienceSummaryTextChange(): void {
    const textValue = this.profileForm.get('experienceSummaryText')?.value || '';
    
    if (textValue.trim()) {
      // Dividir por comas y limpiar espacios
      const items = textValue
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
      
      // Actualizar el formulario
      this.profileForm.get('experienceSummary')?.setValue([...items]);
    } else {
      // Si está vacío, limpiar la lista
      this.profileForm.get('experienceSummary')?.setValue([]);
    }
  }

  removeExperienceSummary(summary: string): void {
    const currentSummaries = this.selectedExperienceSummaries;
    const index = currentSummaries.indexOf(summary);
    if (index > -1) {
      currentSummaries.splice(index, 1);
      this.profileForm.get('experienceSummary')?.setValue([...currentSummaries]);
      this.updateExperienceSummaryText();
    }
  }

  // Método para actualizar el texto cuando se remueve un elemento del resumen
  updateExperienceSummaryText(): void {
    const textValue = this.selectedExperienceSummaries.join(', ');
    this.profileForm.get('experienceSummaryText')?.setValue(textValue);
  }

  loadCoachProfile(): void {
    this.isLoading = true;
    this.loader.show();
    
    console.log('Current User:', this.currentUser);
    console.log('Coach Profile Data:', this.currentUser?.coachProfileData);
    
    if (this.currentUser?.coachProfileData) {
      const profileData = this.currentUser.coachProfileData;
      
      // Cargar datos en los nuevos campos mapeando desde los datos existentes
      const userData = this.currentUser as any;
      
      console.log('User Data:', userData);
      console.log('Profile Data:', profileData);
      
       // Usar firstName y lastName para el campo fullName
       if (userData) {
         const fullName = `${userData.name || ''}`.trim();
         console.log('Full Name:', fullName);
         if (fullName) {
           this.profileForm.get('fullName')?.setValue(fullName);
         }
         
         // Debug: Verificar toda la estructura de datos del usuario
         console.log('Complete userData structure:', userData);
         console.log('userData.personalSection:', userData.personalSection);
         
         // Cargar imagen de perfil si existe
         if (userData.personalSection?.photoBase64) {
           console.log('Profile Image found:', userData.personalSection.photoBase64);
           console.log('Setting profileImageBase64 to:', userData.personalSection.photoBase64);
           this.profileImageBase64 = userData.personalSection.photoBase64;
           console.log('profileImageBase64 after setting:', this.profileImageBase64);
           // Forzar detección de cambios para actualizar la vista
           this.cdr.detectChanges();
         } else {
           console.log('No profile image found in userData.personalSection:', userData.personalSection);
           
           // Buscar la imagen en otras posibles ubicaciones
           if (userData.photoBase64) {
             console.log('Found image in userData.photoBase64:', userData.photoBase64);
             this.profileImageBase64 = userData.photoBase64;
             this.cdr.detectChanges();
           } else {
             // Si no hay imagen, limpiar la variable
             this.profileImageBase64 = null;
             this.cdr.detectChanges();
             console.log('No image found in any expected location');
           }
         }
       }
      
      // Usar professionalTitle para profession
      if (profileData.professionalTitle) {
        console.log('Professional Title:', profileData.professionalTitle);
        this.profileForm.get('profession')?.setValue(profileData.professionalTitle);
      }
      
      // Usar aboutMentor para description
      if (profileData.aboutMentor) {
        console.log('About Mentor:', profileData.aboutMentor);
        this.profileForm.get('description')?.setValue(profileData.aboutMentor);
      }
      
      // Usar contactLinks para email y linkedin
      if (profileData.contactLinks?.email) {
        console.log('Contact Email:', profileData.contactLinks.email);
        this.profileForm.get('email')?.setValue(profileData.contactLinks.email);
      }
      if (profileData.contactLinks?.linkedin) {
        console.log('LinkedIn URL:', profileData.contactLinks.linkedin);
        this.profileForm.get('linkedinUrl')?.setValue(profileData.contactLinks.linkedin);
      }

      // Fallbacks para email y teléfono desde los datos del usuario
      const emailControl = this.profileForm.get('email');
      if ((!emailControl?.value || emailControl.value.trim() === '') && userData?.email) {
        console.log('Setting email from userData.email:', userData.email);
        emailControl?.setValue(userData.email);
      }

      const phoneControl = this.profileForm.get('phone');
      const contactPhone = (profileData.contactLinks as any)?.phone;

      if (userData?.phone) {
        console.log('Setting phone from userData.phone:', userData.phone);
        phoneControl?.setValue(userData.phone);
      } else if (contactPhone && (!phoneControl?.value || phoneControl.value.trim() === '')) {
        console.log('Setting phone from contactLinks.phone:', contactPhone);
        phoneControl?.setValue(contactPhone);
      }
      
            // Cargar logros desde experienceSummary si existe
            if (profileData.experienceSummary && Array.isArray(profileData.experienceSummary) && profileData.experienceSummary.length > 0) {
              console.log('Experience Summary:', profileData.experienceSummary);
              // Limpiar el FormArray y agregar los elementos
              this.achievementsFormArray.clear();
              profileData.experienceSummary.forEach(item => {
                this.achievementsFormArray.push(this.fb.control(item));
              });
              console.log('Achievements loaded:', this.achievementsFormArray.value);
            } else {
              // Inicializar con al menos un elemento vacío si no hay datos
              this.achievementsFormArray.clear();
              if (this.achievementsFormArray.length === 0) {
                this.achievementsFormArray.push(this.fb.control(''));
                console.log('[LoadProfile] Inicializado achievements con un elemento vacío');
              }
            }
      
            // Cargar especialidades si existe información
            if (profileData.specialtyTags && Array.isArray(profileData.specialtyTags) && profileData.specialtyTags.length > 0) {
              console.log('Specialty Tags:', profileData.specialtyTags);
              this.specialtyTagsFormArray.clear();
              profileData.specialtyTags.forEach(tag => {
                this.specialtyTagsFormArray.push(this.fb.control(tag));
              });
              console.log('Specialty tags loaded:', this.specialtyTagsFormArray.value);
            } else {
              // Inicializar con al menos un elemento vacío si no hay datos
              this.specialtyTagsFormArray.clear();
              if (this.specialtyTagsFormArray.length === 0) {
                this.specialtyTagsFormArray.push(this.fb.control(''));
                console.log('[LoadProfile] Inicializado specialtyTags con un elemento vacío');
              }
            }

            // Cargar datos curiosos desde professionalDescription si existe
            if (profileData.professionalDescription && Array.isArray(profileData.professionalDescription) && profileData.professionalDescription.length > 0) {
              console.log('Professional Description:', profileData.professionalDescription);
              // Limpiar el FormArray y agregar los elementos
              this.curiousFactsFormArray.clear();
              profileData.professionalDescription.forEach(item => {
                this.curiousFactsFormArray.push(this.fb.control(item));
              });
              console.log('Curious Facts loaded:', this.curiousFactsFormArray.value);
            } else {
              // Inicializar con al menos un elemento vacío si no hay datos
              this.curiousFactsFormArray.clear();
              if (this.curiousFactsFormArray.length === 0) {
                this.curiousFactsFormArray.push(this.fb.control(''));
                console.log('[LoadProfile] Inicializado curiousFacts con un elemento vacío');
              }
            }
      
      // Cargar eventos y apariciones
      this.eventsAndAppearances.clear();
      this.currentUser.coachProfileData.eventsAndAppearances?.forEach(event => {
        this.eventsAndAppearances.push(this.fb.group(event));
      });
      
      // Cargar el texto de descripciones si existe
      if (this.currentUser.coachProfileData.professionalDescription && 
          Array.isArray(this.currentUser.coachProfileData.professionalDescription)) {
        const textValue = this.currentUser.coachProfileData.professionalDescription.join(', ');
        this.profileForm.get('professionalDescriptionText')?.setValue(textValue);
      }
      
      // Cargar el texto del resumen de experiencia si existe
      if (this.currentUser.coachProfileData.experienceSummary && 
          Array.isArray(this.currentUser.coachProfileData.experienceSummary)) {
        const textValue = this.currentUser.coachProfileData.experienceSummary.join(', ');
        this.profileForm.get('experienceSummaryText')?.setValue(textValue);
      }
    }
    
    // Forzar detección de cambios para actualizar la vista
    this.cdr.detectChanges();
    
     // Verificar que los datos se cargaron correctamente
     setTimeout(() => {
       console.log('[LoadProfile] Final achievements array:', this.achievementsFormArray.value);
       console.log('[LoadProfile] Final curious facts array:', this.curiousFactsFormArray.value);
       console.log('[LoadProfile] Final specialty tags array:', this.specialtyTagsFormArray.value);
       console.log('[LoadProfile] Final profile image:', this.profileImageBase64);
       console.log('[LoadProfile] Estado final del formulario después de cargar datos');
       this.logFormValidation();
       this.cdr.detectChanges();
     }, 100);
    
    this.isLoading = false;
    this.loader.hide();
  }

  // Métodos para manejar dinámicamente los arrays
  addAchievement(): void {
    if (this.achievementsFormArray.length < 5) {
      this.achievementsFormArray.push(this.fb.control(''));
      console.log('[Achievements] Agregado nuevo logro. Total:', this.achievementsFormArray.length);
      this.logFormValidation();
    }
  }

  removeAchievement(index: number): void {
    if (this.achievementsFormArray.length > 1) {
      this.achievementsFormArray.removeAt(index);
      console.log('[Achievements] Removido logro en índice', index, '. Total:', this.achievementsFormArray.length);
      this.logFormValidation();
    }
  }

  addCuriousFact(): void {
    if (this.curiousFactsFormArray.length < 5) {
      this.curiousFactsFormArray.push(this.fb.control(''));
      console.log('[CuriousFacts] Agregado nuevo dato curioso. Total:', this.curiousFactsFormArray.length);
      this.logFormValidation();
    }
  }

  removeCuriousFact(index: number): void {
    if (this.curiousFactsFormArray.length > 1) {
      this.curiousFactsFormArray.removeAt(index);
      console.log('[CuriousFacts] Removido dato curioso en índice', index, '. Total:', this.curiousFactsFormArray.length);
      this.logFormValidation();
    }
  }

  addSpecialtyTag(): void {
    if (this.specialtyTagsFormArray.length < 5) {
      this.specialtyTagsFormArray.push(this.fb.control(''));
      console.log('[SpecialtyTags] Agregada nueva especialidad. Total:', this.specialtyTagsFormArray.length);
      this.logFormValidation();
    }
  }

  removeSpecialtyTag(index: number): void {
    if (this.specialtyTagsFormArray.length > 1) {
      this.specialtyTagsFormArray.removeAt(index);
      console.log('[SpecialtyTags] Removida especialidad en índice', index, '. Total:', this.specialtyTagsFormArray.length);
      this.logFormValidation();
    }
  }

  // Variables para manejo de foto de perfil
  selectedPhotoFile: File | null = null;
  photoChanged: boolean = false;

  // Métodos para manejar la imagen de perfil
  triggerFileInput(): void {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  async onPhotoSelected(event: any): Promise<void> {
    const files = event.target.files as FileList;
    if (files && files.length > 0) {
      this.selectedPhotoFile = files[0];

      // Validar que sea una imagen
      if (!this.selectedPhotoFile.type.startsWith('image/')) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Por favor selecciona un archivo de imagen válido.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#ce0024'
        });
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (this.selectedPhotoFile.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'La imagen no puede ser mayor a 5MB.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#ce0024'
        });
        return;
      }

      try {
        // Convertir el archivo a Base64 usando el servicio
        const base64 = await this.usersService.fileToBase64(this.selectedPhotoFile);
        this.profileImageBase64 = base64;
        this.photoChanged = true;
        this.cdr.detectChanges();
      } catch (error) {
        console.error('Error al convertir la imagen:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al procesar la imagen. Por favor, intenta nuevamente.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#ce0024'
        });
      }
    }
  }

  removeProfileImage(): void {
    this.profileImageBase64 = null;
    this.selectedPhotoFile = null;
    this.photoChanged = true;
    this.cdr.detectChanges();
  }

  /**
   * Sube la foto de perfil al servidor si ha cambiado
   * @returns Promesa que se resuelve cuando se completa la subida
   */
  async uploadProfilePhoto(): Promise<void> {
    if (!this.photoChanged || !this.profileImageBase64) {
      return Promise.resolve();
    }

    try {
      const authUser = this.authService.getAuthUser();
      if (!authUser || !authUser.id) {
        return Promise.reject(new Error('No se pudo obtener el ID del usuario autenticado.'));
      }

      return new Promise((resolve, reject) => {
        this.usersService.updateProfilePhoto(authUser.id, this.profileImageBase64 || '').subscribe({
          next: (response) => {
            this.photoChanged = false;
            resolve();
          },
          error: (err) => {
            console.error('Error al actualizar la foto de perfil:', err);
            reject(err);
          }
        });
      });
    } catch (error) {
      console.error('Error al subir la foto de perfil:', error);
      return Promise.reject(error);
    }
  }

  onChangePassword(): void {
    const email =
      this.profileForm.get('email')?.value ||
      this.currentUser?.email ||
      '';

    if (!email) {
      Swal.fire({
        icon: 'info',
        title: 'Correo requerido',
        text: 'No encontramos un correo asociado. Comunícate con soporte para recuperar tu contraseña.',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#ce0024'
      });
      return;
    }

    if (this.profileForm.get('email')?.invalid) {
      Swal.fire({
        icon: 'warning',
        title: 'Correo inválido',
        text: 'Verifica que el correo electrónico tenga un formato válido antes de continuar.',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#ce0024'
      });
      return;
    }

    const request: RecoverPasswordRequest = { email };

    this.loader.show();
    this.authService.recoverPassword(request).subscribe({
      next: (res: any) => {
        this.loader.hide();
        if (res?.success) {
          Swal.fire({
            title: '¡Instrucciones enviadas!',
            html: `
              <p>${res.message}</p>
              <p style="font-size: 0.9em; color: gray; margin-top: 10px;">
                ¿No recibiste el mensaje? Verifica en el filtro de spam o inténtalo con otro correo electrónico.
              </p>
            `,
            icon: 'success',
            confirmButtonText: 'Continuar',
            confirmButtonColor: '#ce0024'
          });
        } else {
          const message = res?.message || 'No pudimos enviar las instrucciones. Intenta nuevamente.';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: message,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#ce0024'
          });
        }
      },
      error: (err) => {
        this.loader.hide();
        console.error('Error al enviar correo de recuperación:', err);
        const message = err.error?.message || 'Ocurrió un problema al enviar las instrucciones de recuperación.';
        Swal.fire({
          icon: 'error',
          title: 'Error al enviar',
          text: message,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#ce0024'
        });
      }
    });
  }

  async onSubmit(): Promise<void> {
    console.log('[onSubmit] Iniciando envío del formulario');
    console.log('[onSubmit] Formulario válido:', this.profileForm.valid);
    console.log('[onSubmit] Formulario inválido:', this.profileForm.invalid);
    
    if (this.profileForm.invalid) {
      console.log('[onSubmit] Formulario inválido, marcando todos los campos como touched');
      this.logFormValidation();
      this.profileForm.markAllAsTouched();
      return;
    }
    
    console.log('[onSubmit] Formulario válido, procediendo con el guardado');
    this.isLoading = true;
    this.loader.show();

    try {
      // Primero subir la foto de perfil si ha cambiado
      if (this.photoChanged) {
        await this.uploadProfilePhoto();
      }

      const authUser = this.authService.getAuthUser();
      if (!authUser || !authUser.id) {
        console.error('Error: No se pudo obtener el ID del usuario autenticado.');
        this.isLoading = false;
        this.loader.hide();
        Swal.fire({
          icon: 'error',
          title: 'Error Crítico',
          text: 'No se pudo identificar al usuario.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#ce0024'
        });
        return;
      }

      // Crear una copia del formulario sin los campos de texto temporal
      const formValue = { ...this.profileForm.value };
      delete formValue.professionalDescriptionText; // No enviar este campo al backend
      delete formValue.experienceSummaryText; // No enviar este campo al backend
      
       // Mapear los nuevos campos a la estructura existente
       if (formValue.fullName) {
         const nameParts = formValue.fullName.split(' ');
         formValue.firstName = nameParts[0] || '';
         formValue.lastName = nameParts.slice(1).join(' ') || '';
       }
       
       // Agregar imagen de perfil si existe
       if (this.profileImageBase64) {
         formValue.personalSection = {
           ...formValue.personalSection,
           photoBase64: this.profileImageBase64
         };
       }
    
    // Mapear profession a professionalTitle
    if (formValue.profession) {
      formValue.professionalTitle = formValue.profession;
    }
    
    // Mapear description a aboutMentor
    if (formValue.description) {
      formValue.aboutMentor = formValue.description;
    }
    
    // Mapear email y linkedinUrl a contactLinks
    if (formValue.email || formValue.linkedinUrl) {
      formValue.contactLinks = {
        email: formValue.email || '',
        linkedin: formValue.linkedinUrl || '',
        website: formValue.contactLinks?.website || ''
      };
    }
    
     // Mapear achievements a experienceSummary
     const achievementsFiltered = this.achievementsFormArray.value.filter((achievement: string) => achievement && achievement.trim() !== '');
     if (achievementsFiltered.length > 0) {
       formValue.experienceSummary = achievementsFiltered;
     }
     
     // Mapear curiousFacts a professionalDescription
     const curiousFactsFiltered = this.curiousFactsFormArray.value.filter((fact: string) => fact && fact.trim() !== '');
     if (curiousFactsFiltered.length > 0) {
       formValue.professionalDescription = curiousFactsFiltered;
     }
    
    // Limpiar campos temporales del nuevo diseño
    //delete formValue.fullName;
    delete formValue.profession;
    delete formValue.phone;
    delete formValue.linkedinUrl;
    delete formValue.description;
    
    const specialtyTagsFiltered = this.specialtyTagsFormArray.value
      .filter((tag: string) => tag && tag.trim() !== '')
      .map((tag: string) => tag.trim());

    formValue.specialtyTags = specialtyTagsFiltered;

    const formData = {
      _id: authUser.id, // Usar el ID directamente del token decodificado
      coachProfileData: formValue,
      name: formValue.fullName,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phone: formValue.phone,
    };

      this.usersService.editProfile(formData).subscribe({
        next: async (response: BaseResponse<User>) => {
          this.isLoading = false;
          this.loader.hide();
          if (response && response.data) {
            // Refrescar los datos del usuario en el servicio de autenticación
            await this.authService.refreshUserData();
            // Actualizar currentUser con los datos frescos del servicio
            this.currentUser = this.authService.getUser();
            // Recargar el perfil para mostrar la información actualizada
            this.loadCoachProfile();
          }
          // Resetear el flag de foto cambiada después de guardar exitosamente
          this.photoChanged = false;
          Swal.fire({
            icon: 'success',
            title: '¡Éxito!',
            text: 'Perfil de mentor actualizado con éxito.',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#ce0024'
          });
        },
        error: (err: any) => {
          this.isLoading = false;
          this.loader.hide();
          console.error('Error actualizando perfil de mentor:', err);
          const errorMessage = err.error?.message || 'Error al actualizar el perfil. Por favor, inténtelo de nuevo.';
          Swal.fire({
            icon: 'error',
            title: 'Error al Actualizar',
            text: errorMessage,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#ce0024'
          });
        }
      });
    } catch (error) {
      console.error('Error al procesar la foto de perfil:', error);
      this.isLoading = false;
      this.loader.hide();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al procesar la foto de perfil. Por favor, intenta nuevamente.',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#ce0024'
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/dashboard/edit-profile']);
  }

  // Cerrar dropdown al hacer clic fuera
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const multiSelectContainer = target.closest('.multi-select-container');
    
    if (!multiSelectContainer) {
      if (this.showTagsDropdown) {
        this.showTagsDropdown = false;
      }
      if (this.showDescriptionsDropdown) {
        this.showDescriptionsDropdown = false;
      }
    }
  }
} 